import Workflow from 'models/Workflow';

// The Not Selected outcome's why, resolved to the client-configured labels (OLIO-3357): the reason,
// and the follow-up detail when the reason owed one (the free-typed competitor name, else the
// competitor's own lookup label). Returns null when the referral carries no reason — a Selected or
// all-Denied outcome tombstones `not_selected_reason` to nil, so absence is the correct signal that
// there is nothing to show.
//
// Labels fall back to the raw slug so a reason recorded against a since-archived or renamed lookup
// item still renders (mirrors `Workflow#lookupLabel` / the API's Narrate::NotSelectedDetail). The
// competitor detail has no slug fallback — an unresolvable competitor reads as no detail rather than
// a bare slug, matching how the slideout has always shown it.
//
// Shared by the Referral Outcome card and the SNF slideout so the resolution can't drift; each site
// formats the parts its own way (the card as "Reason – <reason>: <detail>", the slideout as
// "<reason> · Competitor: <detail>"). The reason's own typed detail rides unlabelled next to the
// reason at both, because it finishes that sentence rather than standing as a separate fact.

// Mirrors AcceptReferral::MAX_NOT_SELECTED_DETAIL in the api, which rejects anything longer. Held
// here so the form stops a long paste at the keyboard rather than at a 422 — the two have to move
// together, and there is nothing that would catch them drifting apart.
export const MAX_NOT_SELECTED_DETAIL = 120;

// The reason a building lost when one of the client's OWN buildings won. DERIVED, never
// stored: `AcceptReferral#drop_contradictions!` nils `not_selected_reason` on a Selected outcome and
// nils `selected_facility_id` on a Not Selected one, so a run can never carry both. "Not selected
// while the referral still names a winner" IS the reason, and it cannot drift out of step with the
// winner the way a written-once value could.
//
// Named rather than generic because "chose a different facility" reads as a loss to a COMPETITOR,
// the opposite result commercially and the one thing this line exists to report.
//
// Only for a building that did not refuse the patient itself. `facility_outcomes_for` (api
// `accept_referral.rb`) projects every non-winner as not_selected whatever its own determination, so
// a building that DENIED an otherwise-won referral lands in the same bucket, and telling that
// building the patient chose elsewhere in the organisation states the wrong thing about it. Callers
// check the building's own decision first; the slideout has always done so (`SNFSlideout#outcomeRow`).
//
// Falls back to a name-free wording rather than interpolating an empty string, so a profile that has
// not resolved yet reads as vague instead of as "another  facility".
//
// Lives beside resolveNotSelectedReason for the same reason that does: the Referral Outcome card and
// the Insights breakdown both say this sentence, and a second copy is how they start disagreeing.
export function inHouseWinnerReason(clientName?: string | null): string {
  return clientName ? `Patient chose another ${clientName} facility` : 'Patient chose another of our facilities';
}

export interface NotSelectedReason {
  reasonLabel: string;
  detail: string | null;
  // The reason's OWN free text (OLIO-3419) — what it could not say on its own, like which assisted
  // living the patient went to. Separate from `detail` rather than sharing it: the slideout labels
  // that one "Competitor", which this is not. Null whenever the reason did not offer a box or
  // nobody typed in it, since the two are the same nothing to a reader.
  typedDetail: string | null;
}

export function resolveNotSelectedReason(workflow: Workflow): NotSelectedReason | null {
  const reason = workflow.valueFor('not_selected_reason')?.value as string | undefined;
  if (!reason) return null;

  const reasonLabel = workflow.lookupLabel('not_selected_reasons', reason) ?? reason;

  const competitor = workflow.valueFor('competitor')?.value as string | undefined;
  const competitorName = workflow.valueFor('competitor_name')?.value as string | undefined;
  const named = competitorName || workflow.lookupLabel('competitors', competitor);
  const detail = competitor && named ? named : null;

  // Read straight off the value rather than gated on the reason's current flags. The detail box is
  // always shown now (OLIO-3512), and even a reason whose `requiresDetail` was later turned off keeps
  // whatever was typed under it — dropping it would rewrite history, the same way the reason labels
  // fall back to the raw slug above.
  const typedDetail = (workflow.valueFor('not_selected_detail')?.value as string | undefined) || null;

  return { reasonLabel, detail, typedDetail };
}

// Did this building refuse the patient itself? Asked before `inHouseWinnerReason`, because a
// building that denied an otherwise-won referral is projected as not_selected like any other
// non-winner (api `AcceptReferral.facility_outcomes_for`) and would otherwise be told the patient
// chose elsewhere in the organisation, when what happened is that it declined. Its own decision is
// the more specific and more honest answer, so the derived sentence stands down for it.
//
// Reads the run-level value by group rather than the Facility Decision action, matching how this
// panel already resolves a building's decision chip. SNFSlideout asks the same question through
// `facilityDecisionStatus`, which additionally folds in canceled/archived for its status column;
// here only the refusal matters.
export function deniedItself(workflow: Workflow, groupId: string): boolean {
  return workflow.valueFor('facility_determination', { groupId })?.value === 'denied';
}
